import { z } from 'zod';

// コスト項目の型定義（camelCase）
const CostItemModel = z.object({
  name: z.string(),
  currentYearVillageBudget: z.number().nullish(),
  previousYearVillageBudget: z.number().nullish(),
  currentYearExecutionAmount: z.number().nullish(),
  currentYearSelfHelpBudget: z.number().nullish(),
  previousYearExecutionAmount: z.number().nullish(),
  previousYearSelfHelpBudget: z.number().nullish(),
});

// アクティビティの型定義（camelCase）
const ActivityModel = z.object({
  unit: z.string().nullish(),
  title: z.string(),
  currentYearActualValue: z.number().nullish(),
  previousYearActualValue: z.number().nullish(),
});

export const BelanjaProgramModel = z.object({
  id: z.string(),
  name: z.string(),
  currentYearAnggaranAPBD: z.number().nullish(),
  currentYearAnggaranLainnya: z.number().nullish(),
  currentYearRealisasi: z.number().nullish(),
  currentYearRate: z.number().nullish(),
  previousYearAnggaranAPBD: z.number().nullish(),
  previousYearAnggaranLainnya: z.number().nullish(),
  previousYearRealisasi: z.number().nullish(),
  previousYearRate: z.number().nullish(),
  parentId: z.string().uuid().nullish(),
  childIndex: z.number().nullish(),
  createdAt: z.string().nullish(),
});
export const BiayaTenagaModel = z.object({
  id: z.string(),
  name: z.string(),
  currentYearOrg: z.number().nullish(),
  currentYearRp: z.number().nullish(),
  previousYearOrg: z.number().nullish(),
  previousYearRp: z.number().nullish(),
});

export const AlurDanaModel = z.object({
  id: z.string(),
  number: z.number().int(),
  penerimaDana: z.string().nullish(),
  ringkasanKegiatan: z.string().nullish(),
  jumlahDana: z.number().nullish(),
  metodeKontrakDll: z.string().nullish(),
  jumlahPesertaTender: z.number().nullish(),
  tingkatKemenanganTender: z.number().nullish(),
});

// フロントエンド用のcamelCase型定義
export const IndonesiaDataModel = z.object({
  ministryName: z.string().nullish(),
  projectName: z.string().nullish(),
  projectReference: z.string().nullish(),
  legalBasis: z.string().nullish(),
  fiscalYear: z.number().nullish(),
  organizationName: z.string().nullish(),
  peopleInCharge: z.union([z.string(), z.array(z.string())]).nullish(),

  alasanReview: z.string().nullish(),
  tujuanReview: z.string().nullish(),
  target: z.string().nullish(),
  jumlahTargetPenerimaManfaat: z
    .object({
      elementValue: z.number().nullish(),
      elementUnit: z.string().nullish(),
      element2Value: z.number().nullish(),
      element2Unit: z.string().nullish(),
      ratio: z.number().nullish(),
    })
    .array()
    .nullish(),

  background: z.string().nullish(),
  purpose: z.string().nullish(),
  numberOfBeneficiaries: z.number().nullish(),
  totalPopulation: z.number().nullish(),
  overview: z.string().nullish(),
  relatedProjects: z.union([z.string(), z.array(z.string())]).nullish(),

  cost: z.union([z.string(), z.record(z.string(), CostItemModel)]).nullish(),
  costConsideration: z.string().nullish(),
  laborCost: z
    .union([z.string(), z.record(z.string(), CostItemModel)])
    .nullish(),
  totalCost: z
    .union([
      z.string(),
      z.object({
        currentYearExecutionAmount: z.number().nullish(),
        previousYearExecutionAmount: z.number().nullish(),
      }),
    ])
    .nullish(),
  revenueSource: z.string().nullish(),
  costRemark: z.string().nullish(),

  activities: z.union([z.string(), z.array(ActivityModel)]).nullish(),

  analysisOfBudgetUtilization: z.string().nullish(),
  objectiveResultAnalysis: z.string().nullish(),
  evaluationOfProjectResult: z.string().nullish(),
  improvementSuggestions: z.string().nullish(),

  relatedProjectForOtherMinistry: z.string().nullish(),
  projectRemark: z.string().nullish(),

  evaluationOfProgramResults: z.string().nullish(),
  usulanPerbaikan: z.string().nullish(),
  referensiProgramYangSamaDiDaerahLain: z.string().nullish(),
  catatan: z.string().nullish(),

  alurDana: AlurDanaModel.array().nullish(),

  belanjaProgram: BelanjaProgramModel.array().nullish(),

  biayaTenaga: BiayaTenagaModel.array().nullish(),
  totalCurrentYearBiayaTenaga: z.number().nullish(),
  totalPreviousYearBiayaTenaga: z.number().nullish(),

  acuanProgram: z.string().nullish(),
  dasarHukumAtauKebijakan: z.string().nullish(),
});

// APIレスポンス用のsnake_case型定義
const IndonesiaDataResponseModel = z.object({
  // A. Identitas Program
  ministry_name: z.string().nullish(),
  project_name: z.string().nullish(),
  project_reference: z.string().nullish(),
  legal_basis: z.string().nullish(),
  fiscal_year: z.number().nullish(),
  organization_name: z.string().nullish(),
  people_in_charge: z.union([z.string(), z.array(z.string())]).nullish(),

  // B. Ringkasan Program
  background: z.string().nullish(),
  purpose: z.string().nullish(),
  number_of_beneficiaries: z.number().nullish(),
  total_population: z.number().nullish(),
  overview: z.string().nullish(),
  related_projects: z.union([z.string(), z.array(z.string())]).nullish(),

  // C. Biaya Program
  cost: z
    .union([
      z.string(),
      z.record(
        z.string(),
        z.object({
          name: z.string(),
          current_year_village_budget: z.number().nullish(),
          previous_year_village_budget: z.number().nullish(),
          current_year_execution_amount: z.number().nullish(),
          current_year_self_help_budget: z.number().nullish(),
          previous_year_execution_amount: z.number().nullish(),
          previous_year_self_help_budget: z.number().nullish(),
        }),
      ),
    ])
    .nullish(),
  cost_consideration: z.string().nullish(),
  labor_cost: z
    .union([
      z.string(),
      z.record(
        z.string(),
        z.object({
          name: z.string(),
          current_year_village_budget: z.number().nullish(),
          previous_year_village_budget: z.number().nullish(),
          current_year_execution_amount: z.number().nullish(),
          current_year_self_help_budget: z.number().nullish(),
          previous_year_execution_amount: z.number().nullish(),
          previous_year_self_help_budget: z.number().nullish(),
        }),
      ),
    ])
    .nullish(),
  total_cost: z
    .union([
      z.string(),
      z.object({
        current_year_execution_amount: z.number().nullish(),
        previous_year_execution_amount: z.number().nullish(),
      }),
    ])
    .nullish(),
  revenue_source: z.string().nullish(),
  cost_remark: z.string().nullish(),

  // D. Kegiatan
  activities: z
    .union([
      z.string(),
      z.array(
        z.object({
          unit: z.string().nullish(),
          title: z.string(),
          current_year_actual_value: z.number().nullish(),
          previous_year_actual_value: z.number().nullish(),
        }),
      ),
    ])
    .nullish(),

  // E. Analisis dan Evaluasi
  analysis_of_budget_utilization: z.string().nullish(),
  objective_result_analysis: z.string().nullish(),
  evaluation_of_project_result: z.string().nullish(),
  improvement_suggestions: z.string().nullish(),

  // F. Informasi Tambahan
  related_project_for_other_ministry: z.string().nullish(),
  project_remark: z.string().nullish(),
});

export type IndonesiaData = z.infer<typeof IndonesiaDataModel>;
export type IndonesiaDataResponse = z.infer<typeof IndonesiaDataResponseModel>;
export type CostItem = z.infer<typeof CostItemModel>;
export type Activity = z.infer<typeof ActivityModel>;
